import {
  Alert,
  Button,
  InputNumber,
  Modal,
  Radio,
  Space,
  Typography,
  message,
} from 'antd';
import { useMemo, useState } from 'react';
import { useUserStore } from '@entities/user';
import {
  MIN_TOPUP_RUB,
  toNumber,
  useCreateTopupMutation,
} from '@entities/billing';
import { extractErrorMessage } from '@shared/api';

const { Text, Paragraph } = Typography;

interface Props {
  open: boolean;
  onClose: () => void;
}

// Быстрые суммы пополнения (на диалоги). Любая своя сумма от 1000 тоже ок.
const QUICK_AMOUNTS = [15000, 30000, 50000, 100000];
const DEFAULT_TOPUP = 15000;

function fmt(n: number): string {
  return n.toLocaleString('ru-RU');
}

/**
 * Модалка пополнения баланса аккаунта (V2-1: баланс общий для всех ассистентов).
 *
 * Серия C («оплата на выходе»):
 *  - Если у ассистента отрицательный баланс (долг за обработку документов),
 *    показываем разбивку: «Оплата обработки документов» + «Пополнение» = «Итого».
 *    Долг фиксирован (= −balance), юзер редактирует только пополнение.
 *    В оплату (ЮKасса) уходит ИТОГО — webhook прибавит его к минусовому
 *    балансу, долг погасится автоматически (balance = balance + amount).
 *  - Если долга нет — обычная модалка, в оплату уходит просто пополнение.
 *  - Минимум пополнения 1000₽ (тихий) — всплывает только при вводе меньше.
 *    Слово «долг» юзеру не показываем — пишем «Оплата обработки документов».
 */
export function TopupModal({ open, onClose }: Props) {
  // Баланс аккаунта — из user-store (user.balance).
  const balance = useUserStore((s) => toNumber(s.user?.balance));

  // Долг = модуль отрицательного баланса. Фиксирован, юзер не редактирует.
  const debt = balance < 0 ? -balance : 0;
  const hasDebt = debt > 0;

  // Сумма пополнения (на диалоги) — это то, что юзер вводит/выбирает.
  const [topup, setTopup] = useState<number>(DEFAULT_TOPUP);
  const mutation = useCreateTopupMutation();

  // Итого к оплате = долг + пополнение. Уходит в ЮKассу.
  const total = useMemo(() => debt + topup, [debt, topup]);

  const topupTooSmall = topup < MIN_TOPUP_RUB;

  const handlePay = async () => {
    if (topupTooSmall) {
      message.error(`Минимальная сумма пополнения — ${fmt(MIN_TOPUP_RUB)} ₽`);
      return;
    }
    try {
      const result = await mutation.mutateAsync({
        amount_rub: total, // ИТОГО: долг + пополнение
      });
      window.location.assign(result.confirmation_url);
    } catch (err) {
      message.error(extractErrorMessage(err, 'Не удалось создать платёж'));
    }
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      title="Пополнить баланс"
      footer={null}
      destroyOnClose
    >
      <Space direction="vertical" style={{ width: '100%' }} size={16}>
        <Paragraph type="secondary" style={{ marginBottom: 0 }}>
          Баланс общий для всех ваших ассистентов. Оплата через ЮKассу.
        </Paragraph>

        <div>
          <Text strong style={{ display: 'block', marginBottom: 8 }}>
            Быстрый выбор
          </Text>
          <Radio.Group
            value={QUICK_AMOUNTS.includes(topup) ? topup : undefined}
            onChange={(e) => setTopup(e.target.value)}
            optionType="button"
            buttonStyle="solid"
          >
            {QUICK_AMOUNTS.map((a) => (
              <Radio.Button key={a} value={a}>
                {fmt(a)} ₽
              </Radio.Button>
            ))}
          </Radio.Group>
        </div>

        <div>
          <Text strong style={{ display: 'block', marginBottom: 8 }}>
            Или своя сумма
          </Text>
          <InputNumber
            value={topup}
            onChange={(v) => setTopup(v ?? 0)}
            min={0}
            max={1_000_000}
            step={1000}
            style={{ width: '100%' }}
            formatter={(value) =>
              `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ' ')
            }
            parser={(value) => Number((value ?? '').replace(/\s/g, ''))}
            addonAfter="₽"
          />
          {topupTooSmall && (
            <Text type="danger" style={{ fontSize: 12 }}>
              Минимальная сумма пополнения — {fmt(MIN_TOPUP_RUB)} ₽
            </Text>
          )}
        </div>

        {/* Разбивка с долгом — только когда баланс в минусе (серия C) */}
        {hasDebt && (
          <div
            style={{
              background: '#F9FAFB',
              borderRadius: 8,
              padding: 16,
            }}
          >
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                marginBottom: 6,
              }}
            >
              <Text type="secondary" style={{ fontSize: 13 }}>
                Оплата обработки документов
              </Text>
              <Text style={{ fontSize: 13 }}>{fmt(debt)} ₽</Text>
            </div>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                marginBottom: 10,
              }}
            >
              <Text type="secondary" style={{ fontSize: 13 }}>
                Пополнение на диалоги
              </Text>
              <Text style={{ fontSize: 13 }}>{fmt(topup)} ₽</Text>
            </div>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'baseline',
                paddingTop: 10,
                borderTop: '1px solid #E5E7EB',
              }}
            >
              <Text strong style={{ fontSize: 14 }}>
                Итого к оплате
              </Text>
              <Text strong style={{ fontSize: 16 }}>
                {fmt(total)} ₽
              </Text>
            </div>
          </div>
        )}

        {mutation.error && (
          <Alert
            type="error"
            showIcon
            message={extractErrorMessage(mutation.error)}
          />
        )}

        <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
          <Button onClick={onClose} disabled={mutation.isPending}>
            Отмена
          </Button>
          <Button
            type="primary"
            onClick={handlePay}
            loading={mutation.isPending}
            disabled={topupTooSmall}
          >
            Перейти к оплате
          </Button>
        </Space>

        <Text type="secondary" style={{ fontSize: 11 }}>
          Вы будете перенаправлены на защищённую страницу ЮKассы.
          Баланс пополнится автоматически после успешной оплаты.
        </Text>
      </Space>
    </Modal>
  );
}
